/**
 * Token Manager — Handles token expiry, auto-refresh, and 401 error recovery
 *
 * Features:
 * - Stores token + expiry time in localStorage
 * - Auto-refreshes token before expiry (preemptive, not on-demand)
 * - Handles 401 responses by attempting refresh with cross-tab coordination
 * - Guarded logout: only clears tokens if not rotated by another tab
 * - Hard-redirects to /login when session is genuinely ended (auth refresh failure)
 * - Recovers or logs out on tab focus after long idle
 * - Sleep mode recovery: Network detection + exponential backoff retries
 * - Cross-tab sync: Only one tab refreshes at a time, others follow via storage events
 */

// eslint-disable-next-line import/no-cycle
import { acquireRefreshLock } from './tokenRefreshLock';
import logger from './logger';

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';
const REFRESH_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/refresh-token`;

// Singleton state
let refreshPromise = null; // Prevent concurrent refresh calls
let lastFailedRefreshToken = null; // Track which token failed to prevent infinite retry loops
let isInitialized = false; // Prevent duplicate listeners
let isRedirectingToLogin = false;
let isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true; // Network state

const AUTH_PATH_PREFIXES = ['/login'];
const MAX_RETRIES = 4; // Retry up to 4 times: 1s, 2s, 4s, 8s delays
const RETRY_DELAY_BASE = 1000; // 1s base delay for exponential backoff
const FETCH_TIMEOUT_MS = 15000; // 15 seconds timeout for fetch requests

const isOnAuthPage = () => {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '';
  return AUTH_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
};

/**
 * After refresh: redirect on genuine auth failure (401/403)
 * Only logout if tokens were actually cleared (no valid token exists)
 * Transient errors (timeout, network) keep the "retry later" behavior.
 */
const redirectIfRefreshAuthFailed = (result) => {
  // Not an auth error → don't logout
  if (!result || result.success || !result.isAuthError) return;

  // Another tab may have rotated a valid token → don't logout
  const currentToken = getToken();
  if (currentToken) {
    logger.log('[TokenManager] Auth error but valid token exists (from another tab), not logging out');
    return;
  }

  // Auth error + no valid token → user is genuinely logged out
  logger.error('[TokenManager] ❌ Auth error + no valid token → Logging out user');
  redirectToLogin();
};

/**
 * Store tokens + expiry time
 */
export const setTokens = (tokenData) => {
  if (!tokenData || !tokenData.token) {
    clearTokens();
    return;
  }

  try {
    localStorage.setItem(TOKEN_KEY, tokenData.token);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokenData.refresh_token);

    // Clear the failed token flag since we have new valid tokens
    lastFailedRefreshToken = null;

    // Calculate expiry time: now + expires_in (seconds) - 10s buffer (refresh 10s before actual expiry)
    const expiryTime = Date.now() + (tokenData.expires_in - 10) * 1000;
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

    // Schedule auto-refresh
    scheduleTokenRefresh();
  } catch (err) {
    logger.error('Error storing tokens:', err);
  }
};

/**
 * Retrieve stored token
 */
export const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (err) {
    logger.error('Error retrieving token:', err);
    return null;
  }
};

/**
 * Retrieve stored refresh token (internal, for compare-and-swap)
 */
const getRefreshToken = () => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (err) {
    logger.error('Error retrieving refresh token:', err);
    return null;
  }
};

/**
 * Check if token is expired (or expiring soon)
 */
export const isTokenExpired = () => {
  try {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTime) return true;

    const now = Date.now();
    return now >= parseInt(expiryTime, 10);
  } catch (err) {
    logger.error('Error checking token expiry:', err);
    return true;
  }
};

/**
 * Get time remaining until token expiry (in ms)
 */
export const getTokenTimeRemaining = () => {
  try {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTime) return 0;

    const remaining = parseInt(expiryTime, 10) - Date.now();
    return Math.max(0, remaining);
  } catch (err) {
    logger.error('Error getting token time remaining:', err);
    return 0;
  }
};

/**
 * Internal: Perform the actual token refresh (called inside lock)
 * Handles timeouts with AbortController and retries with exponential backoff
 */
const performRefresh = async (retryCount = 0, failedRefreshToken = null) => {
  try {
    const refreshTokenValue = getRefreshToken();
    if (!refreshTokenValue) {
      // CRITICAL: Don't clear immediately - another tab might restore it
      logger.warn('[TokenManager] ⚠️ Refresh token not found, checking if another tab has it...');

      // Wait for another tab to potentially restore it
      await new Promise(resolve => setTimeout(resolve, 500));

      // Check again
      const restoredToken = getRefreshToken();
      if (restoredToken) {
        logger.log('[TokenManager] ✅ Refresh token restored by another tab, retrying with restored token');
        return performRefresh(retryCount + 1, failedRefreshToken);
      }

      // Now it's genuinely missing - safe to clear
      logger.error('[TokenManager] ❌ Refresh token genuinely missing after waiting, clearing tokens');
      clearTokensGuarded(failedRefreshToken);
      return { success: false, error: 'No refresh token', isAuthError: true };
    }

    // Don't attempt refresh if network is offline
    if (!isOnline) {
      logger.log('[TokenManager] Network offline, skipping refresh attempt');
      return { success: false, error: 'Network offline', isAuthError: false };
    }

    logger.log(`[TokenManager] Attempting refresh (attempt ${retryCount + 1}/${MAX_RETRIES + 1}) from:`, REFRESH_ENDPOINT);

    // Use AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

    const response = await fetch(REFRESH_ENDPOINT, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshTokenValue,
      }),
    });

    clearTimeout(timeoutId);

    logger.log('[TokenManager] Refresh response status:', response.status);

    // Handle auth errors (401/403) - IMMEDIATE LOGOUT
    // 401/403 means token is genuinely invalid (not transient error)
    if (response.status === 401 || response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      logger.error('[TokenManager] ❌❌ AUTH FAILURE - 401/403 - LOGGING OUT USER', response.status, errorData);

      // Track this failed token to prevent other tabs from retrying it
      lastFailedRefreshToken = refreshTokenValue;
      clearTokensGuarded(failedRefreshToken || refreshTokenValue);

      // Return auth error flag to trigger immediate logout
      return { success: false, error: `HTTP ${response.status}`, isAuthError: true };
    }

    // Handle server errors (5xx) - transient, retry with backoff
    if (response.status >= 500) {
      if (retryCount < MAX_RETRIES) {
        const delayMs = RETRY_DELAY_BASE * Math.pow(2, retryCount);
        logger.warn(`[TokenManager] ⏳ Server error ${response.status}, retrying in ${delayMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
        return performRefresh(retryCount + 1, failedRefreshToken || refreshTokenValue);
      } else {
        logger.error('[TokenManager] Max retries reached for server error (5xx)');
        return { success: false, error: `HTTP ${response.status} after ${MAX_RETRIES} retries`, isAuthError: false };
      }
    }

    // Other non-2xx errors
    if (!response.ok) {
      logger.warn(`[TokenManager] Refresh token failed with status ${response.status}`);
      const errorBody = await response.text();
      logger.warn('[TokenManager] Error body:', errorBody);
      return { success: false, error: `HTTP ${response.status}`, isAuthError: false };
    }

    const data = await response.json();
    logger.log('[TokenManager] Refresh response data:', data);

    // Extract token data from response (adjust path if API structure differs)
    const newTokenData = data?.data?.data?.token || data?.data?.token || data?.token;
    if (!newTokenData || !newTokenData.token) {
      logger.warn('[TokenManager] Invalid token response structure. Full response:', data);
      return { success: false, error: 'Invalid response', isAuthError: false };
    }

    logger.log('[TokenManager] ✅ Token refreshed successfully');
    setTokens(newTokenData);
    return { success: true, newTokenData };
  } catch (err) {
    // Handle timeout specifically
    if (err.name === 'AbortError') {
      logger.warn('[TokenManager] ⏳ Refresh request timeout');

      if (retryCount < MAX_RETRIES) {
        const delayMs = RETRY_DELAY_BASE * Math.pow(2, retryCount);
        logger.log(`[TokenManager] Timeout - Retrying in ${delayMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
        return performRefresh(retryCount + 1, failedRefreshToken);
      } else {
        logger.error('[TokenManager] Max retries reached for timeout');
        return { success: false, error: 'Timeout', isAuthError: false };
      }
    }

    // Network errors - retry with backoff
    if (retryCount < MAX_RETRIES) {
      const delayMs = RETRY_DELAY_BASE * Math.pow(2, retryCount);
      logger.warn(`[TokenManager] ⏳ Network error (attempt ${retryCount + 1}/${MAX_RETRIES + 1}), retrying in ${delayMs}ms...`, err.message);
      await new Promise(resolve => setTimeout(resolve, delayMs));
      return performRefresh(retryCount + 1, failedRefreshToken);
    } else {
      logger.error('[TokenManager] Max retries reached for network error:', err.message);
      return { success: false, error: err.message, isAuthError: false };
    }
  }
};

/**
 * Refresh token via backend API with cross-tab coordination
 * Uses navigator.locks to ensure only one tab refreshes at a time
 * Returns { success, newTokenData, error, isAuthError } for complex handling
 */
export const refreshToken = async (retryCount = 0) => {
  const result = await acquireRefreshLock(async () => {
    // CRITICAL: After acquiring lock, check if another tab already refreshed the access token
    // This prevents unnecessary refresh calls and race conditions in multi-tab scenarios
    const currentAccessToken = getToken();
    if (currentAccessToken && !isTokenExpired()) {
      logger.log('[TokenManager] ✅ Another tab already refreshed access token, skipping refresh');
      return { success: true, newTokenData: { token: currentAccessToken } };
    }

    const currentRefreshToken = getRefreshToken();

    // LOOP PREVENTION: If this token just failed in another tab, don't retry it
    if (lastFailedRefreshToken && currentRefreshToken === lastFailedRefreshToken) {
      logger.warn('[TokenManager] This token just failed in another tab, skipping retry to prevent infinite loop');
      clearTokensGuarded(currentRefreshToken);
      return { success: false, error: 'Token already failed', isAuthError: true };
    }

    return performRefresh(retryCount, currentRefreshToken);
  });

  if (result === null) {
    logger.warn('[TokenManager] Could not acquire refresh lock, another tab may be refreshing');
    return { success: false, error: 'Refresh in progress', isAuthError: false };
  }

  return result;
};

/**
 * Simpler refresh for interceptor use: returns just the access token or throws
 * Used by axios interceptor for straightforward 401 recovery
 * Delegates to handle401Response() to ensure single-flight across all callers
 *
 * IMPORTANT: Waits for network to be ready before attempting refresh
 */
export const refreshAccessToken = async () => {
  // Wait for network if offline (critical for sleep mode recovery)
  if (!isOnline) {
    logger.log('[TokenManager] Network offline, waiting for reconnection...');
    // Don't throw immediately, let axios retry queue handle it
    throw new Error('Network offline - please wait for connection');
  }

  const { shouldRetry, newToken } = await handle401Response();
  if (shouldRetry && newToken) {
    logger.log('[TokenManager] ✅ Token refresh successful, returning new token');
    return newToken;
  }

  logger.error('[TokenManager] ❌ Token refresh failed or no token available');
  throw new Error('Refresh failed - no valid token');
};

/**
 * GUARDED clear: Only clears tokens if stored refresh token still equals the failed one.
 * Prevents clearing valid tokens rotated by another tab (compare-and-swap pattern).
 *
 * IMPORTANT: Only clears refresh token if genuinely invalid:
 * - Explicit logout (failedRefreshToken = null), OR
 * - Token was rejected by backend (401/403), OR
 * - Token expired naturally (30+ days)
 *
 * Does NOT clear if:
 * - Another tab rotated the token, OR
 * - It's a transient error (network timeout, server error)
 */
const clearTokensGuarded = (failedRefreshToken) => {
  try {
    const currentStoredToken = getRefreshToken();

    // Only clear if no other tab rotated the token
    if (failedRefreshToken && currentStoredToken !== failedRefreshToken) {
      logger.warn(
        '[TokenManager] 🔄 Refresh token was rotated by another tab. Not clearing tokens.'
      );
      return; // Another tab has valid token
    }

    // Token wasn't rotated or explicit logout (failedToken=null) — safe to clear
    logger.error('[TokenManager] ❌ Clearing tokens (genuinely invalid or explicit logout)');
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);

    if (refreshTimeoutId) {
      clearTimeout(refreshTimeoutId);
      refreshTimeoutId = null;
    }

    refreshPromise = null;
    lastFailedRefreshToken = null;
  } catch (err) {
    logger.error('Error clearing tokens:', err);
  }
};

/**
 * Handle 401 response from API — attempt refresh and retry
 * Returns: { shouldRetry: true, newToken: ... } or { shouldRetry: false }
 *
 * Behavior:
 * - 401/403 Auth error: Logout user (tokens cleared, redirect to login)
 * - Network/server errors: Don't clear tokens, preserve for retry
 */
export const handle401Response = async () => {
  // Prevent concurrent refresh attempts within this tab
  if (refreshPromise) {
    const result = await refreshPromise;
    return result;
  }

  refreshPromise = (async () => {
    try {
      const result = await refreshToken();

      if (result.success) {
        logger.log('[TokenManager] ✅ 401 recovery successful, will retry request');
        return { shouldRetry: true, newToken: result.newTokenData.token };
      }

      // 401/403 Auth error
      if (result.isAuthError) {
        logger.error('[TokenManager] ❌ 401/403 Auth error during refresh');

        const currentToken = getToken();
        if (currentToken) {
          // Another tab just refreshed successfully
          logger.log('[TokenManager] Valid token exists (from another tab), will retry request');
          return { shouldRetry: true, newToken: currentToken };
        }

        // No valid token exists → User is genuinely logged out
        logger.error('[TokenManager] ❌❌ NO VALID TOKEN - LOGGING OUT USER');
        redirectToLogin();
        return { shouldRetry: false };
      }

      // Transient error (timeout, network, server error)
      // Preserve tokens and fail the request (user can retry)
      logger.warn('[TokenManager] ⚠️ Transient error during refresh, preserving tokens');
      return { shouldRetry: false };
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Schedule auto-refresh of token before expiry
 */
let refreshTimeoutId = null;

export const scheduleTokenRefresh = () => {
  // Clear existing timeout
  if (refreshTimeoutId) {
    clearTimeout(refreshTimeoutId);
  }

  const timeRemaining = getTokenTimeRemaining();
  if (timeRemaining <= 0) {
    // Token already expired, refresh immediately
    refreshToken()
      .then(redirectIfRefreshAuthFailed)
      .catch(err => logger.error('Immediate refresh failed:', err));
    return;
  }

  // Schedule refresh with a small jitter to avoid thundering herd
  const delayMs = timeRemaining + Math.random() * 1000;

  refreshTimeoutId = setTimeout(() => {
    logger.log(`[TokenManager] Auto-refreshing token at ${new Date().toLocaleTimeString()}...`);
    refreshToken()
      .then(redirectIfRefreshAuthFailed)
      .catch(err => logger.error('[TokenManager] Auto-refresh failed:', err));
  }, delayMs);
};

/**
 * Clear all stored tokens (explicit logout)
 */
export const clearTokens = () => {
  // On explicit logout, clear with no failedToken (forces clear)
  clearTokensGuarded(null);
};

/**
 * Listen for token updates from other tabs via storage events
 */
const setupStorageListener = () => {
  if (typeof window === 'undefined') return;

  const handleStorageChange = (event) => {
    // Detect when another tab logs out (clears access token)
    // BUT: Only logout if refresh token is ALSO cleared (not just access token)
    if (event.key === TOKEN_KEY && event.newValue === null) {
      const hasRefreshToken = getRefreshToken();
      if (!hasRefreshToken) {
        // Both tokens cleared = genuine logout
        logger.log('[TokenManager] Both tokens cleared by another tab (genuine logout), following logout');
        redirectToLogin();
        return;
      } else {
        // Only access token cleared = probably refresh in progress, skip
        logger.log('[TokenManager] Access token cleared but refresh token exists (refresh in progress), skipping logout');
        return;
      }
    }

    // Detect when another tab rotates the refresh token
    if (event.key === REFRESH_TOKEN_KEY && event.newValue && event.newValue !== event.oldValue) {
      logger.log('[TokenManager] Token rotated by another tab, rescheduling auto-refresh');
      scheduleTokenRefresh();
    }

    // Detect when another tab updates token expiry
    if (event.key === TOKEN_EXPIRY_KEY && event.newValue) {
      logger.log('[TokenManager] Token expiry updated by another tab, rescheduling');
      scheduleTokenRefresh();
    }
  };

  try {
    window.addEventListener('storage', handleStorageChange);
    logger.log('[TokenManager] Cross-tab storage listener initialized');
  } catch (err) {
    logger.warn('[TokenManager] Failed to setup storage listener:', err.message);
  }
};

/**
 * Redirect to login page when the session is genuinely ended.
 * Clears tokens, notifies listeners, and hard-navigates so the user
 * is not left on a protected page seeing repeated 401s.
 */
const redirectToLogin = () => {
  try {
    if (isRedirectingToLogin) return;
    if (isOnAuthPage()) {
      clearTokens();
      return;
    }

    isRedirectingToLogin = true;
    logger.warn('Session expired, clearing tokens. Redirect to login.');
    clearTokens();

    // Dispatch custom event that components can listen to
    window.dispatchEvent(new CustomEvent('token-expired'));

    window.location.replace('/login');
  } catch (err) {
    isRedirectingToLogin = false;
    logger.error('Error redirecting to login:', err);
  }
};

/**
 * Monitor network status - critical for sleep mode recovery
 */
const setupNetworkListener = () => {
  if (typeof window === 'undefined') return;

  const handleOnline = async () => {
    logger.log('[TokenManager] 🌐 Network online - attempting recovery refresh');
    isOnline = true;

    // Wait a bit for network to fully stabilize after reconnection
    await new Promise(resolve => setTimeout(resolve, 2000));

    const token = getToken();
    if (token && isTokenExpired()) {
      logger.log('[TokenManager] Token expired, refreshing after network reconnection...');
      const result = await refreshToken();
      if (!result.success && result.isAuthError) {
        logger.warn('[TokenManager] Token invalid after reconnection, logging out');
        redirectToLogin();
      }
    }
  };

  const handleOffline = () => {
    logger.log('[TokenManager] 📡 Network offline - will retry when reconnected');
    isOnline = false;
  };

  try {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    logger.log('[TokenManager] Network listener initialized');
  } catch (err) {
    logger.warn('[TokenManager] Failed to setup network listener:', err.message);
  }
};

/**
 * When user returns to a long-idle tab: recover session or send to login.
 * Handles sleep mode recovery with network checks and proper error handling.
 */
const setupVisibilityListener = () => {
  if (typeof document === 'undefined') return;

  const handleVisibilityChange = async () => {
    if (document.hidden || isOnAuthPage() || isRedirectingToLogin) return;

    logger.log('[TokenManager] Tab returned to focus - checking session...');

    const token = getToken();
    if (!token) {
      // Tokens already cleared (e.g. failed background refresh) — leave protected UI
      logger.warn('[TokenManager] No token on tab focus, redirecting to login');
      redirectToLogin();
      return;
    }

    // Wait for network if currently offline
    if (!isOnline) {
      logger.log('[TokenManager] Network offline on tab focus, will retry when online');
      return;
    }

    // Token still valid, no need to refresh
    if (!isTokenExpired()) {
      logger.log('[TokenManager] Token still valid');
      return;
    }

    // Token expired, attempt recovery
    logger.log('[TokenManager] Token expired on tab focus, attempting refresh...');
    try {
      const result = await refreshToken();
      if (result.success) {
        logger.log('[TokenManager] ✅ Token recovered successfully');
      } else if (result.isAuthError) {
        logger.warn('[TokenManager] ❌ Auth error during recovery');
        redirectToLogin();
      } else {
        logger.warn('[TokenManager] ⚠️ Transient error, will retry on next request');
      }
    } catch (err) {
      logger.error('[TokenManager] Unexpected error during visibility refresh:', err);
    }
  };

  try {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    logger.log('[TokenManager] Visibility listener initialized');
  } catch (err) {
    logger.warn('[TokenManager] Failed to setup visibility listener:', err.message);
  }
};

/**
 * Initialize token manager on app load
 * Call this once in your app's entry point or after login
 */
export const initializeTokenManager = () => {
  if (!isInitialized) {
    setupStorageListener();      // Cross-tab sync
    setupVisibilityListener();   // Tab focus recovery
    setupNetworkListener();      // Sleep mode + network reconnection
    isInitialized = true;
    logger.log('[TokenManager] ✅ Initialized (with sleep mode + cross-tab support)');
  }

  const token = getToken();
  if (token && !isTokenExpired()) {
    logger.log('[TokenManager] Token valid, scheduling auto-refresh');
    scheduleTokenRefresh();
  } else if (token && isTokenExpired()) {
    logger.log('[TokenManager] Token expired on init, attempting refresh...');
    refreshToken().then(result => {
      redirectIfRefreshAuthFailed(result);
      // For transient errors, don't redirect - let user try again on next request
    }).catch(err => {
      logger.error('[TokenManager] Unexpected error during init refresh:', err);
      // Don't redirect on unexpected errors - let user retry
    });
  }
};
